// src/components/partner/PartnerMemberInvitationsPanel.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Clock3,
  Mail,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import {
  fetchPartnerMemberInvitations,
  revokePartnerMemberInvitation,
  sendPartnerMemberInvitation,
} from "../../lib/partner/partnerMemberInvitations";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
  cohort_manager:
    "Cohort manager",
  facilitator: "Facilitator",
  viewer: "Viewer",
};

function statusClasses(status) {
  switch (status) {
    case "pending":
      return "bg-amber-100 text-amber-800";

    case "accepted":
      return "bg-green-100 text-green-800";

    case "expired":
      return "bg-gray-200 text-gray-700";

    case "revoked":
      return "bg-red-100 text-red-700";

    default:
      return "bg-gray-100 text-gray-700";
  }
}

function formatDate(value) {
  if (!value) return "Not available";

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "Not available";
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  ).format(date);
}

export default function PartnerMemberInvitationsPanel({
  partnerId,
  canManage,
  refreshKey = 0,
  onChanged,
}) {
  const [
    invitations,
    setInvitations,
  ] = useState([]);

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [loading, setLoading] =
    useState(true);

  const [actionId, setActionId] =
    useState("");

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  const loadInvitations =
    useCallback(async () => {
      if (!partnerId) return;

      try {
        setLoading(true);
        setError("");

        const rows =
          await fetchPartnerMemberInvitations(
            partnerId
          );

        setInvitations(rows);
      } catch (loadError) {
        console.error(
          "Loading partner team invitations failed:",
          loadError
        );

        setError(
          loadError?.message ||
            "The invitations could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    }, [partnerId]);

  useEffect(() => {
    loadInvitations();
  }, [
    loadInvitations,
    refreshKey,
  ]);

  const filteredInvitations =
    useMemo(() => {
      if (
        statusFilter === "all"
      ) {
        return invitations;
      }

      return invitations.filter(
        (invitation) =>
          invitation.status ===
          statusFilter
      );
    }, [
      invitations,
      statusFilter,
    ]);

  const pendingCount =
    invitations.filter(
      (invitation) =>
        invitation.status ===
        "pending"
    ).length;

  async function handleRevoke(
    invitation
  ) {
    const confirmed =
      window.confirm(
        `Revoke the invitation sent to ${invitation.email}?`
      );

    if (!confirmed) return;

    try {
      setActionId(
        invitation.invitation_id
      );
      setError("");
      setMessage("");

      await revokePartnerMemberInvitation(
        invitation.invitation_id
      );

      setMessage(
        `The invitation for ${invitation.email} was revoked.`
      );

      await loadInvitations();
      await onChanged?.();
    } catch (revokeError) {
      console.error(
        "Revoking partner team invitation failed:",
        revokeError
      );

      setError(
        revokeError?.message ||
          "The invitation could not be revoked."
      );
    } finally {
      setActionId("");
    }
  }

  async function handleResend(
    invitation
  ) {
    try {
      setActionId(
        invitation.invitation_id
      );
      setError("");
      setMessage("");

      await sendPartnerMemberInvitation({
        partnerId:
          invitation.partner_id,

        email:
          invitation.email,

        role:
          invitation.role,

        expiresInDays: 7,
      });

      setMessage(
        `A fresh invitation was sent to ${invitation.email}.`
      );

      await loadInvitations();
      await onChanged?.();
    } catch (resendError) {
      console.error(
        "Resending partner team invitation failed:",
        resendError
      );

      setError(
        resendError?.message ||
          "A fresh invitation could not be sent."
      );
    } finally {
      setActionId("");
    }
  }

  return (
    <section className="mt-6 overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
      <header className="flex flex-col gap-4 border-b border-[#EFEFEF] px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Partner access
          </p>

          <h2 className="mt-1 text-xl font-black">
            Team invitations
          </h2>

          <p className="mt-1 text-sm text-[#757575]">
            {pendingCount} pending
            invitation
            {pendingCount === 1
              ? ""
              : "s"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(
                event.target.value
              )
            }
            className="rounded-full border border-[#DDDDDD] bg-white px-4 py-2 text-sm font-black outline-none focus:border-[#FF914D]"
          >
            <option value="all">
              All invitations
            </option>

            <option value="pending">
              Pending
            </option>

            <option value="accepted">
              Accepted
            </option>

            <option value="expired">
              Expired
            </option>

            <option value="revoked">
              Revoked
            </option>
          </select>

          <button
            type="button"
            onClick={
              loadInvitations
            }
            disabled={loading}
            aria-label="Refresh invitations"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#DDDDDD] text-[#555555] transition hover:bg-[#F7F7F7] disabled:opacity-50"
          >
            <RefreshCw
              className={[
                "h-4 w-4",
                loading
                  ? "animate-spin"
                  : "",
              ].join(" ")}
            />
          </button>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="m-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      )}

      {message && (
        <div
          role="status"
          className="m-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
        >
          {message}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm font-semibold text-[#757575]">
          Loading team invitations…
        </div>
      ) : filteredInvitations.length ===
        0 ? (
        <div className="p-8 text-center">
          <Mail className="mx-auto h-7 w-7 text-[#FF914D]" />

          <p className="mt-3 font-black">
            No invitations found
          </p>

          <p className="mt-1 text-sm text-[#757575]">
            Invitations matching this
            status will appear here.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#EFEFEF]">
          {filteredInvitations.map(
            (invitation) => {
              const acting =
                actionId ===
                invitation.invitation_id;

              return (
                <article
                  key={
                    invitation.invitation_id
                  }
                  className="flex flex-col gap-4 px-5 py-5 lg:flex-row lg:items-center"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                      <ShieldCheck className="h-5 w-5" />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate font-black">
                        {
                          invitation.email
                        }
                      </p>

                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#FFF4EC] px-3 py-1 text-xs font-black text-[#E9782F]">
                          {ROLE_LABELS[
                            invitation.role
                          ] ||
                            invitation.role}
                        </span>

                        <span
                          className={[
                            "rounded-full px-3 py-1 text-xs font-black capitalize",
                            statusClasses(
                              invitation.status
                            ),
                          ].join(" ")}
                        >
                          {
                            invitation.status
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="min-w-[210px] text-sm text-[#757575]">
                    <p className="inline-flex items-center">
                      <Clock3 className="mr-2 h-4 w-4" />

                      Expires{" "}
                      {formatDate(
                        invitation.expires_at
                      )}
                    </p>

                    <p className="mt-1 text-xs">
                      Invited by{" "}
                      {invitation.invited_by_name ||
                        invitation.invited_by_email ||
                        "Gwin"}
                    </p>
                  </div>

                  {canManage && (
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      {invitation.status ===
                        "pending" &&
                        invitation.can_revoke && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRevoke(
                                invitation
                              )
                            }
                            disabled={
                              acting
                            }
                            className="inline-flex items-center rounded-full border border-red-200 px-4 py-2 text-xs font-black text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Revoke
                          </button>
                        )}

                      {invitation.status !==
                        "accepted" && (
                        <button
                          type="button"
                          onClick={() =>
                            handleResend(
                              invitation
                            )
                          }
                          disabled={
                            acting
                          }
                          className="inline-flex items-center rounded-full border border-[#FF914D] px-4 py-2 text-xs font-black text-[#FF914D] transition hover:bg-[#FFF4EC] disabled:opacity-50"
                        >
                          <RotateCcw className="mr-2 h-4 w-4" />

                          {acting
                            ? "Sending…"
                            : "Resend"}
                        </button>
                      )}
                    </div>
                  )}
                </article>
              );
            }
          )}
        </div>
      )}
    </section>
  );
}